import useBulkActions from "@entities/brief/lib/hooks/useBulkActions";
import { useDownloadBriefXlsx } from "@entities/brief/lib/hooks/useDownloadBriefXlsx";
import { useGetBriefDataFields } from "@entities/brief/lib/hooks/useGetBriefDataFields";
import { useGetBriefDeliverables } from "@entities/brief/lib/hooks/useGetBriefDeliverables";
import { useParseEditCreatorsCsv } from "@entities/brief/lib/hooks/useParseEditCreatorsCsv";
import { useSyncBriefGoogleSheets } from "@entities/brief/lib/hooks/useSyncBriefGoogleSheets";
import useGetDeliverableConfigurators from "@entities/deliverableConfigurator/lib/hooks/useGetDeliverableConfigurators";
import { useBriefStore } from "@pages/layout/BriefLayout/lib/useLayoutStore";
import { ToastService } from "@shared/lib/services";
import {
  BriefNotificationActionType,
  IParseEditCreatorsGoogleSheetRequest,
} from "@shared/lib/types/brief";
import { useEffect, useMemo, useRef, useState } from "react";
import { collectCustomFields } from "../../lib/constants";
import {
  isProcessedEditRow,
  toEditBriefInfluencerParams,
} from "./lib/constants";
import { ReviewTab } from "./ui/ProcessStep";

interface UseBulkEditModalParams {
  isOpen: boolean;
  onClose: () => void;
  onSuccess?: () => void;
}

export function useBulkEditModal({
  isOpen,
  onClose,
  onSuccess,
}: UseBulkEditModalParams) {
  const { brief, briefId, setBrief } = useBriefStore();
  const [step, setStep] = useState<"upload" | "process">("upload");
  const [reviewTab, setReviewTab] = useState<ReviewTab>("processed");

  const [parsedRows, setParsedRows] = useState<
    IParseEditCreatorsGoogleSheetRequest[]
  >([]);
  const didRequestImport = useRef(false);

  const { syncGoogleSheet, isSyncing, isSynced, syncedBrief } =
    useSyncBriefGoogleSheets(briefId);
  const { downloadBriefXlsx, isDownloadingXlsx } = useDownloadBriefXlsx();
  const { parseEditCreatorsCsv, isParsingEditCreatorsCsv } =
    useParseEditCreatorsCsv((rows) => {
      setParsedRows(rows);
      setReviewTab("processed");
      setStep("process");
    });
  const { bulkActions, isBulkActions, isBulkActionsSuccess } = useBulkActions();
  const { deliverables, isFetching: isFetchingDeliverables } =
    useGetBriefDeliverables(isOpen ? briefId : "");
  const hasNoDeliverables =
    !isFetchingDeliverables && (deliverables?.length ?? 0) === 0;
  const { dataFields } = useGetBriefDataFields(isOpen ? briefId : "");
  const { deliverableConfiguratorsData } = useGetDeliverableConfigurators();
  const configurators = deliverableConfiguratorsData?.data?.data || [];

  const customFields = useMemo(
    () =>
      collectCustomFields([
        dataFields?.customFields ?? [],
        brief?.customFieldsV2 ?? [],
        brief?.dataFieldsV2?.filter((field) => field.isSpecialField) ?? [],
      ]),
    [brief?.customFieldsV2, brief?.dataFieldsV2, dataFields?.customFields]
  );

  const templateSheetId =
    brief?.editCreatorsGoogleSheetId ||
    syncedBrief?.data?.editCreatorsGoogleSheetId;

  const processedRows = useMemo(
    () => parsedRows.filter(isProcessedEditRow),
    [parsedRows]
  );

  useEffect(() => {
    if (!isOpen || !briefId) return;
    syncGoogleSheet({ type: "editCreators" });
  }, [isOpen, briefId]);

  useEffect(() => {
    if (isSynced && syncedBrief?.data) {
      setBrief(syncedBrief.data, briefId);
    }
  }, [briefId, isSynced, setBrief, syncedBrief]);

  useEffect(() => {
    if (!isOpen) {
      didRequestImport.current = false;

      setTimeout(() => {
        setStep("upload");
        setReviewTab("processed");
        setParsedRows([]);
      }, 300);
    }
  }, [isOpen]);

  useEffect(() => {
    if (!isBulkActionsSuccess || !didRequestImport.current) return;
    didRequestImport.current = false;
    ToastService.success("Updating creators on this brief…");
    onSuccess?.();
    onClose();
  }, [isBulkActionsSuccess, onClose, onSuccess]);

  const handleImport = () => {
    if (!briefId || processedRows.length === 0) return;
    didRequestImport.current = true;
    bulkActions({
      briefId,
      actionType: BriefNotificationActionType.editBriefInfluencer,
      ids: processedRows.map((row) => row.briefInfluencerId),
      filterParams: {},
      actionParams: {},
      isSelectAll: false,
      notIncludeIds: [],
      singleActionParams: processedRows.map((row) => ({
        editBriefInfluencerParams: toEditBriefInfluencerParams(row),
      })),
    });
  };

  return {
    briefId,
    step,
    setStep,
    reviewTab,
    setReviewTab,

    parsedRows,
    isSyncing,
    isDownloadingXlsx,
    isParsingEditCreatorsCsv,
    isBulkActions,
    deliverables,
    hasNoDeliverables,
    configurators,
    customFields,
    templateSheetId,
    processedRows,
    downloadBriefXlsx,
    parseEditCreatorsCsv,
    handleImport,
  };
}
